import * as THREE from "three";
import { isSolid } from "../world/blocks";
import { sound } from "../audio/sound-engine";
import type { Animals } from "./animals";
import type { Hostiles } from "./hostiles";
import type { World } from "../world/world";

export interface Arrow {
  readonly id: number;
  readonly position: THREE.Vector3;
  readonly velocity: THREE.Vector3;
  yaw: number;
  pitch: number;
  readonly damage: number;
  readonly owner: "player" | "skeleton";
  stuck: boolean;
  stuckBlockPos: THREE.Vector3 | null;
  lifetime: number;
  canPickup: boolean;
}

export interface ProjectileUpdateResult {
  readonly playerDamage: number;
  readonly collectedArrows: number;
}

export const ARROW_GRAVITY = 18;
export const MAX_ARROWS = 120;
export const ARROW_PICKUP_RADIUS = 1.35;
export const ARROW_DESPAWN_STUCK = 60; // seconds before stuck arrows disappear
export const ARROW_DESPAWN_FLIGHT = 15; // seconds max in flight

export class Projectiles {
  private readonly arrows: Arrow[] = [];
  private nextId = 1;

  private readonly rayOrigin = new THREE.Vector3();
  private readonly rayDir = new THREE.Vector3();
  private readonly playerBox = new THREE.Box3();
  private readonly playerRay = new THREE.Ray();
  private readonly boxHit = new THREE.Vector3();

  constructor(
    private readonly world: World,
    private readonly animals: Animals,
    private readonly hostiles: Hostiles,
  ) {}

  get activeArrows(): readonly Arrow[] {
    return this.arrows;
  }

  spawnArrow(
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    speed: number,
    damage: number,
    owner: "player" | "skeleton" = "player",
    canPickup = true,
  ): Arrow | null {
    if (this.arrows.length >= MAX_ARROWS) {
      this.arrows.shift();
    }

    const normDir = direction.clone().normalize();
    const velocity = normDir.clone().multiplyScalar(speed);
    const yaw = Math.atan2(normDir.x, normDir.z);
    const pitch = Math.atan2(-normDir.y, Math.hypot(normDir.x, normDir.z));

    const arrow: Arrow = {
      id: this.nextId++,
      position: origin.clone(),
      velocity,
      yaw,
      pitch,
      damage,
      owner,
      stuck: false,
      stuckBlockPos: null,
      lifetime: 0,
      canPickup,
    };

    this.arrows.push(arrow);
    return arrow;
  }

  update(
    dt: number,
    playerPosition: THREE.Vector3,
    playerSurvival = true,
  ): ProjectileUpdateResult {
    let playerDamage = 0;
    let collectedArrows = 0;

    for (let i = this.arrows.length - 1; i >= 0; i--) {
      const arrow = this.arrows[i];
      arrow.lifetime += dt;

      if (arrow.stuck) {
        // Despawn after timeout
        if (arrow.lifetime > ARROW_DESPAWN_STUCK) {
          this.arrows.splice(i, 1);
          continue;
        }

        // Check if supporting block was destroyed
        if (arrow.stuckBlockPos) {
          const block = this.world.getBlock(
            arrow.stuckBlockPos.x,
            arrow.stuckBlockPos.y,
            arrow.stuckBlockPos.z,
          );
          if (!isSolid(block)) {
            arrow.stuck = false;
            arrow.stuckBlockPos = null;
            arrow.velocity.set(0, -1, 0);
          }
        }

        // Player pickup check
        if (arrow.canPickup && arrow.position.distanceTo(playerPosition) < ARROW_PICKUP_RADIUS) {
          collectedArrows++;
          this.arrows.splice(i, 1);
          continue;
        }

        continue;
      }

      // Despawn if in flight too long or below world
      if (arrow.lifetime > ARROW_DESPAWN_FLIGHT || arrow.position.y < -10) {
        this.arrows.splice(i, 1);
        continue;
      }

      // Ballistic physics step
      const step = Math.min(dt, 0.05);
      const remainingDist = arrow.velocity.length() * step;
      if (remainingDist < 1e-5) continue;

      this.rayOrigin.copy(arrow.position);
      this.rayDir.copy(arrow.velocity).normalize();

      // Check collision along flight trajectory
      let hitMob = false;

      // 1. Check player collision (if shot by skeleton)
      if (arrow.owner !== "player") {
        this.playerBox.min.set(
          playerPosition.x - 0.35,
          playerPosition.y,
          playerPosition.z - 0.35,
        );
        this.playerBox.max.set(
          playerPosition.x + 0.35,
          playerPosition.y + 1.8,
          playerPosition.z + 0.35,
        );
        this.playerRay.set(this.rayOrigin, this.rayDir);
        const hit = this.playerRay.intersectBox(this.playerBox, this.boxHit);
        if (hit && this.rayOrigin.distanceTo(hit) <= remainingDist) {
          if (playerSurvival) {
            playerDamage += arrow.damage;
          }
          this.arrows.splice(i, 1);
          continue;
        }
      }

      // 2. Check hostile mobs collision (if shot by player)
      if (arrow.owner === "player") {
        const hostileHit = this.hostiles.raycast(this.rayOrigin, this.rayDir, remainingDist);
        if (hostileHit) {
          this.hostiles.damage(hostileHit.hostile, arrow.damage, this.rayOrigin);
          this.arrows.splice(i, 1);
          hitMob = true;
          continue;
        }
      }

      // 3. Check animal collision
      const animalHit = this.animals.raycast(this.rayOrigin, this.rayDir, remainingDist);
      if (animalHit) {
        this.animals.damage(animalHit.animal, arrow.damage, this.rayOrigin);
        this.arrows.splice(i, 1);
        hitMob = true;
        continue;
      }

      if (hitMob) continue;

      // 4. Check solid block collision
      const blockHit = this.world.raycast(
        this.rayOrigin,
        this.rayDir,
        remainingDist,
        (id) => isSolid(id),
      );

      if (blockHit) {
        // Embed slightly at collision point
        const hitPoint = this.rayOrigin.addScaledVector(this.rayDir, blockHit.distance);
        arrow.position.copy(hitPoint);
        arrow.velocity.set(0, 0, 0);
        arrow.stuck = true;
        arrow.stuckBlockPos = new THREE.Vector3(blockHit.x, blockHit.y, blockHit.z);
        sound.playArrowHitBlock();
        continue;
      }

      // Free flight displacement
      arrow.position.addScaledVector(arrow.velocity, step);
      arrow.velocity.y -= ARROW_GRAVITY * step;
      const drag = Math.pow(0.99, step * 60);
      arrow.velocity.x *= drag;
      arrow.velocity.z *= drag;

      arrow.yaw = Math.atan2(arrow.velocity.x, arrow.velocity.z);
      arrow.pitch = Math.atan2(-arrow.velocity.y, Math.hypot(arrow.velocity.x, arrow.velocity.z));
    }

    return { playerDamage, collectedArrows };
  }

  clear(): void {
    this.arrows.length = 0;
  }
}
